import { createDefaultTransport, type DetectTransportOptions } from "#bridge/transports/detect";
import type { BridgeCapabilities, BridgeTransport } from "#bridge/types";
import type { Handler } from "#emitter";
import { createEmitter } from "#emitter";
import type { SdkEnvelope } from "#envelope";
import type { InboundEvents, OutboundEvents, RpcMethods } from "#events";
import { createEventMessage, createRequestId, createRequestMessage, createResponseErrMessage, createResponseOkMessage } from "#message";

export type RequestOptions = {
	timeoutMs?: number;
};

export type AnswerHandler<TType extends keyof RpcMethods> = (payload: RpcMethods[TType]["req"]) => RpcMethods[TType]["res"] | Promise<RpcMethods[TType]["res"]>;

export type CreateBridgeOptions = DetectTransportOptions & {
	transport?: BridgeTransport;
	defaultRequestTimeoutMs?: number;
};

type Pending = {
	resolve: (value: unknown) => void;
	reject: (error: Error) => void;
	timer: ReturnType<typeof setTimeout>;
};

export type Bridge = {
	readonly capabilities: BridgeCapabilities;
	readonly connected: boolean;

	on<Key extends keyof InboundEvents>(type: Key, handler: Handler<InboundEvents[Key]>): () => void;
	once<Key extends keyof InboundEvents>(type: Key, handler: Handler<InboundEvents[Key]>): () => void;
	waitFor<Key extends keyof InboundEvents>(type: Key): Promise<InboundEvents[Key]>;
	off<Key extends keyof InboundEvents>(type: Key, handler?: Handler<InboundEvents[Key]>): void;

	/** Fire-and-forget web → wrapper */
	emit<Key extends keyof OutboundEvents>(type: Key, payload: OutboundEvents[Key]): void;

	/** RPC web → wrapper */
	request<Key extends keyof RpcMethods>(type: Key, payload: RpcMethods[Key]["req"], options?: RequestOptions): Promise<RpcMethods[Key]["res"]>;

	/** Handle RPC wrapper → web */
	answer<Key extends keyof RpcMethods>(type: Key, handler: AnswerHandler<Key>): () => void;

	whenConnected(timeoutMs?: number): Promise<void>;
	destroy(): void;
};

export function createBridge(options: CreateBridgeOptions = {}): Bridge {
	const transport = options.transport ?? createDefaultTransport(options);
	const defaultRequestTimeoutMs = options.defaultRequestTimeoutMs ?? 10_000;

	const bus = createEmitter<InboundEvents>();
	const pending = new Map<string, Pending>();
	const answers = new Map<string, AnswerHandler<keyof RpcMethods>>();

	const settle = (id: string, result: { ok: true; payload: unknown } | { ok: false; error: Error }) => {
		const entry = pending.get(id);
		if (!entry) return;
		clearTimeout(entry.timer);
		pending.delete(id);
		if (result.ok) entry.resolve(result.payload);
		else entry.reject(result.error);
	};

	const unsubscribe = transport.subscribe((message: SdkEnvelope) => {
		if (message.kind === "event") {
			bus.emit(message.type as keyof InboundEvents, message.payload as InboundEvents[keyof InboundEvents]);
			return;
		}

		if (message.kind === "response") {
			if (message.ok) {
				settle(message.id, { ok: true, payload: message.payload });
			} else {
				settle(message.id, {
					ok: false,
					error: new Error(`[${message.error.code}] ${message.error.message}`),
				});
			}
			return;
		}

		if (message.kind === "request") {
			void handleInboundRequest(message);
		}
	});

	async function handleInboundRequest(message: Extract<SdkEnvelope, { kind: "request" }>) {
		const handler = answers.get(message.type);
		if (!handler) {
			transport.send(
				createResponseErrMessage(message.type, message.id, {
					code: "UNHANDLED",
					message: `No answer handler for ${message.type}`,
				}),
			);
			return;
		}

		try {
			const payload = await handler(message.payload as never);
			transport.send(createResponseOkMessage(message.type, message.id, payload));
		} catch (error) {
			transport.send(
				createResponseErrMessage(message.type, message.id, {
					code: "HANDLER_ERROR",
					message: error instanceof Error ? error.message : String(error),
				}),
			);
		}
	}

	const instance: Bridge = {
		get capabilities() {
			return transport.capabilities;
		},
		get connected() {
			return transport.connected;
		},

		on: bus.on.bind(bus),
		once: bus.once.bind(bus),
		waitFor: bus.waitFor.bind(bus),
		off: bus.off.bind(bus),

		emit(type, payload) {
			if (!transport.capabilities.outbound) {
				console.warn(`[bridge] emit(${String(type)}) ignored: outbound=false`);
				return;
			}
			transport.send(createEventMessage(type, payload));
		},

		async request(type, payload, requestOptions) {
			if (!transport.capabilities.rpc || !transport.capabilities.outbound) {
				throw new Error(`bridge.request(${String(type)}) unsupported in this environment`);
			}

			const timeoutMs = requestOptions?.timeoutMs ?? defaultRequestTimeoutMs;
			await transport.whenConnected(timeoutMs);

			const id = createRequestId();
			const message = createRequestMessage(type, id, payload);

			const result = new Promise<RpcMethods[typeof type]["res"]>((resolve, reject) => {
				const timer = setTimeout(() => {
					pending.delete(id);
					reject(new Error(`bridge.request(${String(type)}) timed out after ${timeoutMs}ms`));
				}, timeoutMs);

				pending.set(id, {
					resolve: resolve as (value: unknown) => void,
					reject,
					timer,
				});
			});

			transport.send(message);
			return result;
		},

		answer(type, handler) {
			const typed = handler as unknown as AnswerHandler<keyof RpcMethods>;
			answers.set(type, typed);
			return () => {
				if (answers.get(type) === typed) answers.delete(type);
			};
		},

		whenConnected(timeoutMs) {
			return transport.whenConnected(timeoutMs);
		},

		destroy() {
			unsubscribe();
			for (const [id, entry] of pending) {
				clearTimeout(entry.timer);
				entry.reject(new Error("bridge destroyed"));
				pending.delete(id);
			}
			answers.clear();
			transport.destroy();
		},
	};

	return instance;
}
